/*
  RBT Visuals – "Choisis ta moto" finder and catalogue brand groups.
  The kit cards (snippets/rbt-kit-card.liquid) carry their compatibility as data-* attributes,
  written from the product metafields rbt.*. Everything here is read from those cards: the
  finder never offers a capacity, brand, model or year that no kit covers.

  <rbt-kit-finder data-scope="#…" data-mode="results|filter">
    results: home page. Cards stay hidden until a capacity and a brand are chosen.
    filter:  catalogue. Every card is shown; each choice narrows them down and empty brand
             groups disappear.
  The choice is kept in the address (?cc=125&marque=Yamaha&annee=2022) so it survives a
  reload, the back button and a shared link, without any redirect.
*/
(() => {
  const ANY = 'Toutes marques';
  const nf = (n) => (n === 0 ? 'Électrique' : `${n} cc`);
  const uniq = (list) => [...new Set(list)];
  const params = new URLSearchParams(location.search);

  /* Catalogue: show `data-visible` kits per brand, the rest behind "Voir les N autres". */
  const collapse = (group, on) => {
    const limit = Number(group.dataset.visible) || 8;
    const button = group.querySelector('[data-more]');
    if (!button) return;
    group.querySelectorAll('.rbt-kit').forEach((card, i) => {
      card.toggleAttribute('data-folded', on && i >= limit);
    });
    group.querySelectorAll('[data-sub]').forEach((sub) => {
      sub.hidden = on && !sub.querySelector('.rbt-kit:not([data-folded])');
    });
    button.hidden = !on;
  };

  const initGroups = (root) => {
    root.querySelectorAll('[data-group]').forEach((group) => {
      const button = group.querySelector('[data-more]');
      if (!button) return;
      // A link to this brand (#marque-yamaha) opens it in full.
      collapse(group, location.hash !== `#${group.id}`);
      button.addEventListener('click', () => {
        const firstHidden = group.querySelector('.rbt-kit[data-folded]');
        collapse(group, false);
        firstHidden?.querySelector('a')?.focus({ preventScroll: true });
      });
    });

    // Brand bar: underline the brand being read and keep it in view in the swipe row.
    const links = new Map(
      [...root.querySelectorAll('.rbt-cat__nav-link')].map((a) => [a.getAttribute('href').slice(1), a])
    );
    if (links.size && 'IntersectionObserver' in window) {
      const spy = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const link = links.get(entry.target.id);
            if (!link || link.classList.contains('is-current')) return;
            links.forEach((a) => a.classList.remove('is-current'));
            link.classList.add('is-current');
            const row = link.closest('ul');
            row.scrollTo({ left: link.offsetLeft - row.clientWidth / 2 + link.offsetWidth / 2, behavior: 'smooth' });
          });
        },
        { rootMargin: '-30% 0px -60% 0px' }
      );
      links.forEach((_, id) => {
        const group = document.getElementById(id);
        if (group) spy.observe(group);
      });
    }

    // A brand link while the finder hides that brand: clear the finder first.
    root.addEventListener('click', (event) => {
      const link = event.target.closest('a[href^="#marque-"]');
      const group = link && document.getElementById(link.getAttribute('href').slice(1));
      if (group?.hidden) group.closest('.rbt-cat')?.querySelector('rbt-kit-finder')?.reset(false);
    });
  };

  class RbtKitFinder extends HTMLElement {
    connectedCallback() {
      this.scope = document.querySelector(this.dataset.scope) || this;
      this.mode = this.dataset.mode || 'results';
      this.cards = [...this.scope.querySelectorAll('.rbt-kit[data-brand]')].map((el) => ({
        el,
        semi: el.dataset.gamme === 'semi',
        // "Toutes marques" products (e.g. the 100 % perso plate background) fit every brand.
        any: el.dataset.brand === ANY,
        brand: el.dataset.brand,
        model: el.dataset.model,
        cat: el.dataset.cat,
        ccs: el.dataset.cc.split(',').filter(Boolean).map(Number),
        y1: el.dataset.y1 ? Number(el.dataset.y1) : null,
        y2: el.dataset.y2 ? Number(el.dataset.y2) : null,
      }));
      if (!this.cards.length) return;
      // Words used in the status line ("kit" by default, "fond de plaque" on plate pages).
      this.noun = this.dataset.noun || 'kit';
      this.nouns = this.dataset.nouns || `${this.noun}s`;
      this.hasDedicated = this.cards.some((c) => !c.semi && !c.any);

      // Category of each model ("Yamaha|MT-125" → Roadster): a semi-perso design (no model,
      // cross only) fits a chosen model only when that model is a cross bike.
      this.modelCats = new Map();
      this.cards.forEach((c) => {
        if (!c.model) return;
        const key = `${c.brand}|${c.model}`;
        if (!this.modelCats.has(key)) this.modelCats.set(key, new Set());
        this.modelCats.get(key).add(c.cat);
      });

      this.select = {};
      this.querySelectorAll('[data-select]').forEach((s) => (this.select[s.dataset.select] = s));
      this.modelField = this.querySelector('[data-field="model"]');
      this.status = this.querySelector('[data-status]');
      this.bar = this.querySelector('[data-bar]');
      this.empty = this.querySelector('[data-empty]');
      this.results = this.scope.querySelector('[data-results]');

      this.fill('cc', uniq(this.cards.flatMap((c) => c.ccs)).sort((a, b) => (a || 1e4) - (b || 1e4)), nf);

      this.select.cc.addEventListener('change', () => this.update('cc'));
      this.select.brand.addEventListener('change', () => this.update('brand'));
      this.select.model.addEventListener('change', () => this.update('model'));
      this.select.year.addEventListener('change', () => this.update('year'));
      this.querySelectorAll('[data-reset]').forEach((b) => b.addEventListener('click', () => this.reset()));
      this.querySelector('[data-missing]').addEventListener('click', () => this.showMissing());

      this.querySelector('[data-fields]').hidden = false;
      this.bar.hidden = false;

      const wanted = { cc: params.get('cc'), brand: params.get('marque'), model: params.get('modele'), year: params.get('annee') };

      // Helmets: Marque → Modèle → Taille, no capacity or year. Every kit is made in every
      // size, so the size only adds a line confirming it.
      this.casque = this.dataset.kind === 'casque';
      if (this.casque) {
        const brands = this.brandOrder(uniq(this.cards.map((c) => c.brand)));
        this.fill('brand', brands);
        this.select.size?.addEventListener('change', () => this.render());
        if (brands.length === 1) this.select.brand.value = brands[0];
        else if (wanted.brand && this.has('brand', wanted.brand)) this.select.brand.value = wanted.brand;
        if (this.select.brand.value) {
          this.update('brand', false);
          if (wanted.model && this.has('model', wanted.model)) {
            this.select.model.value = wanted.model;
            this.update('model', false);
          }
        }
        return;
      }

      // Restore a choice from the address.
      if (wanted.cc && this.has('cc', wanted.cc)) {
        this.select.cc.value = wanted.cc;
        this.update('cc', false);
        if (wanted.brand && this.has('brand', wanted.brand)) {
          this.select.brand.value = wanted.brand;
          this.update('brand', false);
          if (wanted.model && this.has('model', wanted.model)) {
            this.select.model.value = wanted.model;
            this.update('model', false);
          }
          if (wanted.year && this.has('year', wanted.year)) {
            this.select.year.value = wanted.year;
            this.update('year', false);
          }
        }
      }
    }

    has(name, value) {
      return [...this.select[name].options].some((o) => o.value === value);
    }

    fill(name, values, label = (v) => v, first) {
      const s = this.select[name];
      s.length = 1;
      if (first) s.options[0].textContent = first;
      values.forEach((v) => s.add(new Option(label(v), v)));
      s.disabled = values.length === 0;
    }

    state() {
      return {
        cc: this.select.cc.value === '' ? null : Number(this.select.cc.value),
        brand: this.select.brand.value,
        model: this.select.model.value,
        year: this.select.year.value === '' ? null : Number(this.select.year.value),
      };
    }

    matches(card, s, upTo = 'year') {
      if (s.cc !== null && !card.ccs.includes(s.cc)) return false;
      if (upTo === 'cc') return true;
      if (s.brand && card.brand !== s.brand && !card.any) return false;
      if (upTo === 'brand') return true;
      if (s.model && card.model !== s.model) {
        if (card.model) return false;
        if (!this.modelCats.get(`${card.brand}|${s.model}`)?.has(card.cat)) return false;
      }
      if (upTo === 'model') return true;
      // Kits without years (PW 50, Stark Varg, scooters, semi-perso) fit every year.
      if (s.year !== null && card.y1 !== null && (s.year < card.y1 || s.year > card.y2)) return false;
      return true;
    }

    update(changed, record = true) {
      const s = this.state();

      if (changed === 'cc') {
        const brands = uniq(this.cards.filter((c) => !c.any && this.matches(c, s, 'cc')).map((c) => c.brand));
        this.fill('brand', this.brandOrder(brands));
        this.select.brand.disabled = s.cc === null;
        // Brand page: only one brand to pick, so pick it.
        if (s.cc !== null && brands.length === 1) this.select.brand.value = brands[0];
        this.select.model.value = '';
        this.modelField.hidden = true;
        this.fill('year', [], undefined, 'Toutes');
        this.select.year.disabled = true;
      }

      if (changed === 'cc' || changed === 'brand') {
        const now = this.state();
        const models = now.brand
          ? uniq(this.cards.filter((c) => c.model && this.matches(c, now, 'brand')).map((c) => c.model))
          : [];
        // Only offer the model when the brand has several in this capacity. It stays optional:
        // the kits show as soon as the brand is known, model and year narrow them down.
        this.fill('model', models.length > 1 ? models : [], undefined, 'Tous');
        this.modelField.hidden = models.length < 2;
        // Visible steps: (cylindrée,) marque, [modèle,] année or taille.
        this.querySelector('[data-fields]').dataset.count = (this.casque ? 2 : 3) + (this.modelField.hidden ? 0 : 1);
        this.fillYears();
      }

      if (changed === 'model') this.fillYears();

      this.render();
      if (record) this.record();
    }

    fillYears() {
      const s = this.state();
      const pool = s.brand ? this.cards.filter((c) => this.matches(c, s, 'model') && c.y1 !== null) : [];
      const years = [];
      if (pool.length) {
        // Every year between the oldest and newest kit, so a year with no kit gives an honest
        // "pas de kit dédié" instead of silently missing from the list.
        const max = Math.min(Math.max(...pool.map((c) => c.y2)), new Date().getFullYear() + 1);
        for (let y = max; y >= Math.min(...pool.map((c) => c.y1)); y--) years.push(y);
      }
      this.fill('year', years, undefined, 'Toutes');
    }

    brandOrder(brands) {
      // Same order as the catalogue: the order the cards come in.
      const order = uniq(this.cards.map((c) => c.brand));
      return brands.sort((a, b) => order.indexOf(a) - order.indexOf(b));
    }

    ready(s) {
      return (this.casque || s.cc !== null) && Boolean(s.brand);
    }

    render() {
      const s = this.state();
      const active = this.casque ? Boolean(s.brand) : s.cc !== null;
      const ready = this.ready(s);
      const show = this.mode === 'filter' ? active : ready;
      let count = 0;
      let semis = 0;
      let anys = 0;

      this.cards.forEach((c) => {
        const on = this.mode === 'filter' ? !active || this.matches(c, s) : show && this.matches(c, s);
        c.el.hidden = !on;
        if (on) count++;
        if (on && c.semi) semis++;
        else if (on && c.any) anys++;
      });
      const dedicated = count - semis - anys;

      if (this.mode === 'filter') {
        this.scope.querySelectorAll('[data-group]').forEach((group) => {
          collapse(group, !active);
          group.querySelectorAll('[data-sub]').forEach((sub) => {
            if (active) sub.hidden = !sub.querySelector('.rbt-kit:not([hidden])');
          });
          group.hidden = active && !group.querySelector('.rbt-kit:not([hidden])');
        });
      }
      if (this.results) this.results.hidden = !show || count === 0;

      this.empty.hidden = !(show && count === 0);
      this.querySelector('.rbt-finder__links [data-reset]').hidden = !active;

      const moto = [s.brand, s.model, s.cc !== null ? nf(s.cc) : '', s.year || ''].filter(Boolean).join(' ');
      const target = this.casque ? `ton casque ${moto}` : `ta ${moto}`;
      const size = this.casque && this.select.size?.value;
      if (!active) {
        this.status.textContent = '';
      } else if (!ready && this.mode === 'results') {
        this.status.textContent = '';
      } else if (count === 0) {
        this.status.textContent = `Aucun kit dédié pour ${moto}.`;
      } else if (!ready) {
        this.say(`${count} ${count > 1 ? this.nouns : this.noun}`, ` en ${moto}.`);
      } else {
        // Never blur "made for this moto" (100 % perso) and "fits it" (semi-perso).
        const parts = [];
        if (dedicated) parts.push(`${dedicated} ${dedicated > 1 ? this.nouns : this.noun} 100 % perso dédié${dedicated > 1 ? 's' : ''}`);
        if (semis) parts.push(`${semis} semi-perso adaptable${semis > 1 ? 's' : ''}`);
        if (anys) parts.push(`${anys} ${anys > 1 ? this.nouns : this.noun} toutes marques`);
        const left = [];
        if (!this.modelField.hidden && !s.model) left.push('le modèle');
        if (s.year === null && !this.select.year.disabled) left.push('l’année');
        const hint = dedicated > 1 && left.length ? ` Précise ${left.join(' et ')} pour ne garder que le bon.` : '';
        const fit = size ? ` Taille ${size} : disponible, comme toutes les tailles.` : '';
        if (dedicated || !this.hasDedicated) {
          this.say(parts.join(' et '), ` pour ${target}.${fit || hint}`);
        } else {
          this.say(parts.join(' et '), ` pour ${target}. Pas encore de ${this.noun} 100 % perso dédié à cette moto.`);
        }
      }
    }

    say(strongText, rest) {
      const strong = document.createElement('strong');
      strong.textContent = strongText;
      this.status.replaceChildren(strong, rest);
    }

    record() {
      const s = this.state();
      const url = new URL(location.href);
      ['cc', 'marque', 'modele', 'annee'].forEach((k) => url.searchParams.delete(k));
      if (s.cc !== null) url.searchParams.set('cc', s.cc);
      if (s.brand) url.searchParams.set('marque', s.brand);
      if (s.model) url.searchParams.set('modele', s.model);
      if (s.year !== null) url.searchParams.set('annee', s.year);
      history.replaceState(history.state, '', url);
    }

    reset(focus = true) {
      if (this.casque) {
        if (this.select.brand.length > 2) this.select.brand.value = '';
        this.select.model.value = '';
        if (this.select.size) this.select.size.value = '';
        this.update('brand');
        if (focus) this.select.brand.focus();
        return;
      }
      this.select.cc.value = '';
      this.update('cc');
      if (focus) this.select.cc.focus();
    }

    showMissing() {
      this.empty.hidden = false;
      this.empty.focus();
    }
  }

  // Groups first: a choice restored from the address then overrides their folding.
  const start = () => {
    initGroups(document);
    if (!customElements.get('rbt-kit-finder')) customElements.define('rbt-kit-finder', RbtKitFinder);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
